import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../../api';
import { DeviceRole } from '../../types/models';
import { ApiResponseRoles } from '../../types/api/ApiResponseRoles';

interface RolesContextType {
  roles: DeviceRole[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  getRoleBySlug: (slug: string) => DeviceRole | undefined;
  getRoleById: (id: number) => DeviceRole | undefined;
}

const RolesContext = createContext<RolesContextType | undefined>(undefined);

export function RolesProvider({ children }: { children: React.ReactNode }) {
  const [roles, setRoles] = useState<DeviceRole[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const fetchInProgress = useRef(false);

  const fetchRoles = useCallback(async () => {
    if (fetchInProgress.current) {
      return;
    }
    fetchInProgress.current = true;

    try {
      setIsLoading(true);
      setError(null);

      const response = await apiFetch('/datacenters/roles');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseRoles;
      if (data.status === 'success' && data.data?.roles) {
        setRoles(data.data.roles);
      } else {
        setRoles([]);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An error occurred while fetching roles'));
    } finally {
      setIsLoading(false);
      fetchInProgress.current = false;
    }
  }, [apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated && auth.user) {
      fetchRoles();
    }
  }, [auth.isAuthenticated]);

  const getRoleBySlug = useCallback((slug: string) => roles.find((role) => role.slug === slug), [roles]);

  const getRoleById = useCallback((id: number) => roles.find((role) => role.id === id), [roles]);

  const value = useMemo<RolesContextType>(
    () => ({ roles, isLoading, error, refetch: fetchRoles, getRoleBySlug, getRoleById }),
    [roles, isLoading, error, fetchRoles, getRoleBySlug, getRoleById],
  );

  return <RolesContext.Provider value={value}>{children}</RolesContext.Provider>;
}

export function useRoles() {
  const context = useContext(RolesContext);
  if (context === undefined) {
    throw new Error('useRoles must be used within a RolesProvider');
  }
  return context;
}
